/**
 * Home — public marketing landing page.
 *
 * Audience: dual — software providers and hardware providers considering
 * joining the Simkura platform. Deliberately short: an outcome-led hero
 * ("Secure your doors from anywhere") with direct dual-audience CTAs, an
 * auto-rotating carousel of the units published on the platform (read from
 * the anonymous /public/catalog endpoint), then the "Join the platform" cards
 * for whoever needs more than the hero CTA to act. Product detail, hardware
 * facts, and credentials live on /product and /hardware — this page only
 * routes visitors to the right side.
 *
 * Nav + footer live in PublicLayout; this page only declares its content.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import {
  IconArrowRight,
  IconAntenna,
  IconBattery3,
  IconShieldCheck,
  IconApi,
  IconBuildingFactory2,
  IconChevronLeft,
  IconChevronRight,
  IconDoor,
  IconId,
  IconPlug,
} from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';
import { getPublicCatalog, publicFileUrl, publicListingPath, VARIANT_STATUS_LABELS } from '../../services/listings';
import type { PublicProvider, PublicListingSummary, VariantStatus } from '../../services/listings';

// Brand navy — hero accent and CTA fill.
const NAVY = '#0d253d';

// ── Layout primitives ─────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 clamp(20px, 6vw, 64px);
`;

const Section = styled.section`
  padding: clamp(40px, 7vh, 80px) 0;
`;

// ── Hero ──────────────────────────────────────────────────────────────────────

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(56px, 11vh, 120px) 0 clamp(32px, 5vh, 56px);

  /* Soft colour wash — the only section with a painted backdrop, so the page
     opens with colour and settles into the canvas. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(60% 80% at 20% 0%, #d8e4f0 0%, transparent 60%),
      radial-gradient(50% 70% at 85% 10%, #dddcea 0%, transparent 55%),
      radial-gradient(40% 50% at 60% 100%, #c9d6df22 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const HeroHeadline = styled.h1`
  font-size: clamp(38px, 6.5vw, 66px);
  line-height: 1.03;
  letter-spacing: -0.028em;
  font-weight: 700;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  max-width: 17ch;

  em {
    font-style: normal;
    color: ${NAVY};
  }
`;

const HeroSub = styled.p`
  font-size: clamp(16px, 2vw, 19px);
  line-height: 1.55;
  color: ${({ theme }) => theme.font.color.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing(6)};
  max-width: 44ch;
`;

// Quick facts as chips — replaces a paragraph of qualifiers.
const HeroChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.secondary};

  svg { color: ${NAVY}; }
`;

// ── Hero CTAs ───────────────────────────────────────────────────────────────
// Dual-audience actions right in the hero — "connect your software" /
// "connect your hardware" — so a visitor who already knows which side they're
// on can act immediately, without waiting for the "Join the platform" cards
// further down the page.

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(6)};
`;

const HeroCtaPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 50px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: ${NAVY};
  color: #fff;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 15px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

const HeroCtaSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 50px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  border: 1px solid ${NAVY};
  color: ${NAVY};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 15px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover { background: ${({ theme }) => theme.background.primary}; }
`;

// ── Units on the platform — auto-rotating product carousel ───────────────────
// Every published product from the public catalog, one card each. The track
// advances one card at a time on a timer, pauses while the visitor hovers or
// focuses it, and stops entirely under prefers-reduced-motion.

const UNITS_INTERVAL_MS = 4000;
const UNITS_GAP_PX = 20;

const UnitsSection = styled.section`
  padding: 0 0 clamp(40px, 7vh, 80px);
`;

const UnitsHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  margin: 0 0 ${({ theme }) => theme.spacing(5)} 0;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  h2 {
    font-size: clamp(22px, 3vw, 30px);
    line-height: 1.12;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0 0 ${({ theme }) => theme.spacing(1)} 0;
  }
  p {
    font-size: 15px;
    color: ${({ theme }) => theme.font.color.tertiary};
    margin: 0;
    max-width: 52ch;
  }
  a {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${NAVY};
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    font-size: 14px;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const Carousel = styled.div`
  position: relative;
`;

const Viewport = styled.div`
  overflow: hidden;
  /* Room for the card lift on hover without clipping it. */
  padding: 4px;
  margin: -4px;
`;

const Track = styled.div<{ index: number; perView: number }>`
  display: flex;
  gap: ${UNITS_GAP_PX}px;
  transform: translateX(calc(-${({ index }) => index} * (100% + ${UNITS_GAP_PX}px) / ${({ perView }) => perView}));
  transition: transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Slide = styled.div<{ perView: number }>`
  flex: 0 0 calc((100% - ${({ perView }) => (perView - 1) * UNITS_GAP_PX}px) / ${({ perView }) => perView});
  min-width: 0;
`;

const UnitCard = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: ${({ theme }) => theme.border.radius.xl};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  background: ${({ theme }) => theme.background.primary};
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover, &:focus-visible {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(13, 37, 61, 0.10);
    outline: none;
  }

  .image {
    aspect-ratio: 4 / 3;
    background: #eef3f8;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: contain; }
    span {
      font-size: 13px;
      font-weight: ${({ theme }) => theme.font.weight.semiBold};
      letter-spacing: 0.04em;
      color: ${({ theme }) => theme.font.color.tertiary};
    }
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(4)};
    flex: 1;
  }

  .provider {
    font-size: 12px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.font.color.tertiary};
  }

  h3 {
    font-size: 18px;
    line-height: 1.25;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0;
  }

  .blurb {
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.font.color.secondary};
    margin: 0;
    flex: 1;
  }

  .facts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  .view {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: ${({ theme }) => theme.spacing(2)};
    font-size: 14px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${NAVY};
  }
`;

const Fact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: #eef3f8;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.secondary};

  svg { color: ${NAVY}; }
`;

const StatusPill = styled.span<{ tone: 'live' | 'soon' }>`
  position: absolute;
  top: 12px;
  left: 12px;
  height: 24px;
  padding: 0 10px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  background: ${({ tone }) => (tone === 'live' ? '#dcf5e7' : '#fff1d6')};
  color: ${({ tone }) => (tone === 'live' ? '#116b3a' : '#8a5a00')};
`;

const CarouselFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const ArrowButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  background: ${({ theme }) => theme.background.primary};
  color: ${NAVY};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: #eef3f8; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: ${({ active }) => (active ? 22 : 8)}px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ active, theme }) => (active ? NAVY : theme.border.color.medium)};
  transition: width 0.2s ease, background 0.2s ease;
`;

const UnitsEmpty = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${({ theme }) => theme.font.color.tertiary};
`;

interface UnitCardData {
  key: string;
  to: string;
  hero: number | null;
  fallback: string;
  name: string;
  provider: string;
  blurb: string | null;
  doors: number;
  power: string;
  cellular: boolean;
  /** Reader technologies the board pairs with, as buyer-facing labels. */
  credentials: string[];
  status: VariantStatus;
}

const READER_TECHNOLOGY_LABELS: Record<string, string> = {
  prox: 'Prox',
  smartcard: 'Smart card',
  nfc: 'NFC',
  ble: 'Mobile',
  multi: 'Multi-tech',
};

function credentialLabels(supported: Record<string, string[]> | null | undefined): string[] {
  const techs = supported?.['doors.reader.technology'] ?? [];
  return techs.map((t) => READER_TECHNOLOGY_LABELS[t] ?? t);
}

/** One card per product; the bare board stands in when it has no products. Discontinued units are left out. */
function unitCards(providers: PublicProvider[]): UnitCardData[] {
  const fromListing = (listing: PublicListingSummary): UnitCardData[] => {
    const base = {
      provider: listing.provider_name,
      doors: listing.default_num_doors,
      power: listing.default_power_type,
      cellular: listing.capabilities.includes('connectivity'),
      credentials: credentialLabels(listing.supported),
    };
    if (listing.variants.length === 0) {
      return [{
        ...base,
        key: listing.slug,
        to: publicListingPath(listing.provider_slug, listing.slug),
        hero: listing.hero_asset_id,
        fallback: listing.board,
        name: listing.display_name || `${listing.provider_name} ${listing.board}`,
        blurb: listing.headline,
        status: 'available',
      }];
    }
    return listing.variants
      .filter((v) => v.status !== 'discontinued')
      .map((v) => ({
        ...base,
        key: `${listing.slug}/${v.slug}`,
        to: publicListingPath(listing.provider_slug, listing.slug, v.slug),
        hero: v.hero_asset_id ?? listing.hero_asset_id,
        fallback: v.sku,
        name: v.name,
        blurb: v.headline || listing.headline,
        status: v.status,
      }));
  };
  return providers.flatMap((p) => p.listings.flatMap(fromListing));
}

function powerLabel(power: string): string {
  switch (power) {
    case 'battery': return 'Battery';
    case 'plugin': return 'Mains';
    case 'solar': return 'Solar';
    default: return power.charAt(0).toUpperCase() + power.slice(1);
  }
}

/** Cards visible at once, tracking the viewport width. */
function usePerView(): number {
  const compute = () => {
    if (typeof window === 'undefined') return 3;
    if (window.matchMedia('(max-width: 640px)').matches) return 1;
    if (window.matchMedia('(max-width: 960px)').matches) return 2;
    return 3;
  };
  const [perView, setPerView] = useState(compute);
  useEffect(() => {
    const onResize = () => setPerView(compute());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return perView;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function UnitsCarousel() {
  const { data: providers = [], isLoading } = useQuery({
    queryKey: ['public-catalog'],
    queryFn: getPublicCatalog,
    staleTime: 5 * 60 * 1000,
  });
  const cards = useMemo(() => unitCards(providers), [providers]);
  const perView = usePerView();
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const maxIndex = Math.max(0, cards.length - perView);
  const pageCount = maxIndex + 1;

  // Keep the index valid when the viewport or the card list changes.
  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  // Auto-rotate: one card at a time, wrapping to the start after the last page.
  useEffect(() => {
    if (paused || reducedMotion || pageCount <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, UNITS_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [paused, reducedMotion, pageCount, maxIndex]);

  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  const next = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));

  if (isLoading) return null;

  return (
    <UnitsSection aria-label="Units on the platform">
      <Container>
        <UnitsHead>
          <div>
            <h2>Units on the platform</h2>
            <p>Hardware you can manage through the Simkura API.</p>
          </div>
          <Link to="/hardware">See all hardware <IconArrowRight size={15} /></Link>
        </UnitsHead>

        {cards.length === 0 ? (
          <UnitsEmpty>No hardware is listed yet. Providers publish their own listings from the dashboard.</UnitsEmpty>
        ) : (
          <Carousel
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
          >
            <Viewport>
              <Track index={index} perView={perView} aria-live="off">
                {cards.map((card, i) => {
                  const visible = i >= index && i < index + perView;
                  return (
                    <Slide key={card.key} perView={perView} aria-hidden={!visible}>
                      <UnitCard to={card.to} tabIndex={visible ? 0 : -1}>
                        <div className="image" style={{ position: 'relative' }}>
                          {card.hero
                            ? <img src={publicFileUrl(card.hero)} alt={card.name} loading="lazy" />
                            : <span>{card.fallback}</span>}
                          <StatusPill tone={card.status === 'available' ? 'live' : 'soon'}>
                            {VARIANT_STATUS_LABELS[card.status]}
                          </StatusPill>
                        </div>
                        <div className="body">
                          <span className="provider">{card.provider}</span>
                          <h3>{card.name}</h3>
                          {card.blurb && <p className="blurb">{card.blurb}</p>}
                          <div className="facts">
                            <Fact><IconDoor size={13} /> {card.doors} door{card.doors === 1 ? '' : 's'}</Fact>
                            <Fact><IconPlug size={13} /> {powerLabel(card.power)}</Fact>
                            {card.cellular && <Fact><IconAntenna size={13} /> Cellular</Fact>}
                            {card.credentials.length > 0 && (
                              <Fact><IconId size={13} /> {card.credentials.join(', ')}</Fact>
                            )}
                          </div>
                          <span className="view">View product <IconArrowRight size={14} /></span>
                        </div>
                      </UnitCard>
                    </Slide>
                  );
                })}
              </Track>
            </Viewport>

            {pageCount > 1 && (
              <CarouselFoot>
                <ArrowButton type="button" onClick={prev} aria-label="Previous units">
                  <IconChevronLeft size={18} />
                </ArrowButton>
                <Dots role="tablist" aria-label="Carousel position">
                  {Array.from({ length: pageCount }, (_, i) => (
                    <Dot
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={i === index}
                      aria-label={`Go to position ${i + 1}`}
                      active={i === index}
                      onClick={() => setIndex(i)}
                    />
                  ))}
                </Dots>
                <ArrowButton type="button" onClick={next} aria-label="Next units">
                  <IconChevronRight size={18} />
                </ArrowButton>
              </CarouselFoot>
            )}
          </Carousel>
        )}
      </Container>
    </UnitsSection>
  );
}

// ── Join the platform — two audience cards ────────────────────────────────────

const JoinHead = styled.div`
  margin: 0 0 ${({ theme }) => theme.spacing(6)} 0;

  h2 {
    font-size: clamp(24px, 3.4vw, 34px);
    line-height: 1.12;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
  }
  p {
    font-size: 15px;
    color: ${({ theme }) => theme.font.color.tertiary};
    margin: 0;
    max-width: 52ch;
  }
`;

const JoinGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const JoinCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.border.radius.xl};
  padding: clamp(24px, 4vw, 36px);
  background: #d8e4f0;

  .ic {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.background.primary};
    color: ${NAVY};
  }
  h3 {
    font-size: 20px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
  }
  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.55;
    color: ${({ theme }) => theme.font.color.secondary};
    flex: 1;
  }
`;

const JoinLink = styled(Link)`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: 46px;
  padding: 0 22px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: ${({ theme }) => theme.background.primary};
  color: ${({ theme }) => theme.font.color.primary};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 15px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

function Home() {
  return (
    <PublicLayout>
      {/* Hero — colour wash, one sentence, quick-fact chips, dual CTAs */}
      <HeroSection>
        <Container>
          <Hero>
            <HeroHeadline>
              Secure your doors <em>from anywhere</em>.
            </HeroHeadline>
            <HeroSub>
              {/* Keep this spot for tag line */}
            </HeroSub>

            <HeroActions>
              <HeroCtaPrimary to="/developers">
                Connect your software
                <IconArrowRight size={16} />
              </HeroCtaPrimary>
              <HeroCtaSecondary to="/hardware">
                Connect your hardware
                <IconArrowRight size={16} />
              </HeroCtaSecondary>
            </HeroActions>
          </Hero>
        </Container>
      </HeroSection>

      {/* Units on the platform — auto-rotating carousel of published products */}
      <UnitsCarousel />

      {/* Join the platform — one card per provider audience */}
      <Section>
        <Container>
          <JoinHead>
            <h2>Join the platform</h2>
            <p>Two ways to work with Simkura, depending on what you build.</p>
          </JoinHead>
          <JoinGrid>
            <JoinCard>
              <span className="ic"><IconApi size={24} strokeWidth={1.5} /></span>
              <h3>Software providers</h3>
              <p>
                Integrate you access control platform to connect to any Simkura enabled lock
              </p>
              <JoinLink to="/developers">
                Explore software integrations
                <IconArrowRight size={16} />
              </JoinLink>
            </JoinCard>
            <JoinCard>
              <span className="ic"><IconBuildingFactory2 size={24} strokeWidth={1.5} /></span>
              <h3>Hardware providers</h3>
              <p>
                Join the Simkura API pipeline — bring your own hardware and
                reach software providers already building on Simkura.
              </p>
              <JoinLink to="/hardware">
                Join as a hardware provider
                <IconArrowRight size={16} />
              </JoinLink>
            </JoinCard>
          </JoinGrid>
        </Container>
      </Section>
    </PublicLayout>
  );
}

export default Home;
